// frontend/src/services/pushNotifications.ts
//
// OneSignal push wiring (frontend half of NOTIFICATIONS_SPEC.md).
// Platform-branched: native (Capacitor iOS) uses onesignal-cordova-plugin;
// web uses react-onesignal (OneSignal v16 web SDK + the service worker in
// /public/OneSignalSDKWorker.js).
//
// Identity model: OneSignal external_user_id == our user.id (set on login,
// cleared on logout). The backend targets pushes by external id, so we store
// no device tokens ourselves and multi-device works for free.
//
// Safe-by-default: every function no-ops unless VITE_ONESIGNAL_APP_ID is set,
// so environments without the env var (local dev, preview) are unaffected.
// Permission is requested AFTER login, never on first paint.
import { Capacitor } from '@capacitor/core';

const APP_ID: string | undefined = import.meta.env.VITE_ONESIGNAL_APP_ID;
// Legacy-Safari web push id, auto-generated by OneSignal for this app (public,
// not a secret). Only used when init actually runs (i.e. APP_ID is set).
const SAFARI_WEB_ID = 'web.onesignal.auto.41aef4b6-51a4-4778-92bc-f3aa3e26acde';

let initPromise: Promise<void> | null = null;
let nativeSDK: any = null; // onesignal-cordova-plugin default export
let webSDK: any = null;    // react-onesignal default export

/** Backend sends `additionalData.path` (e.g. /leagues/3/my-team?focus=game-picks).
 *  Navigating by full path keeps the handler dumb and the routing decisions
 *  server-side, per the spec's deep-link design. */
function handleNotificationClick(event: any) {
  try {
    const path = event?.notification?.additionalData?.path;
    if (typeof path === 'string' && path.startsWith('/')) {
      window.location.assign(path);
    }
  } catch {
    /* never let a malformed payload break the app */
  }
}

/** Initialize the SDK for the current platform. Called at app start AND
 *  awaited by pushLogin — callers share one promise so login can never race
 *  ahead of a still-loading SDK. Cheap no-op when the env var is absent. */
export function initPush(): Promise<void> {
  if (!APP_ID) {
    console.info('[push] VITE_ONESIGNAL_APP_ID not set; push disabled in this build.');
    return Promise.resolve();
  }
  if (!initPromise) initPromise = doInit();
  return initPromise;
}

async function doInit(): Promise<void> {
  try {
    if (Capacitor.isNativePlatform()) {
      console.info('[push] initializing OneSignal (native).');
      const mod = await import('onesignal-cordova-plugin');
      nativeSDK = mod.default ?? mod;
      nativeSDK.initialize(APP_ID);
      nativeSDK.Notifications.addEventListener('click', handleNotificationClick);
    } else {
      console.info('[push] initializing OneSignal (web).');
      const mod = await import('react-onesignal');
      webSDK = mod.default ?? mod;
      await webSDK.init({
        appId: APP_ID,
        safariWebId: SAFARI_WEB_ID,
        allowLocalhostAsSecureOrigin: true,
      });
      try {
        webSDK.Notifications.addEventListener('click', handleNotificationClick);
      } catch {
        /* web clicks usually navigate via the notification url anyway */
      }
    }
    console.info('[push] OneSignal initialized.');
  } catch (e) {
    console.warn('[push] OneSignal init failed (non-fatal):', e);
  }
}

/** Tie this device's subscription to our user id, then ask permission.
 *  Called on login AND session restore (idempotent on OneSignal's side). */
export async function pushLogin(externalUserId: string): Promise<void> {
  if (!APP_ID) return;
  try {
    await initPush(); // shared promise: waits for the actual init, never races it
    if (Capacitor.isNativePlatform()) {
      if (!nativeSDK) return; // init failed (already warned)
      nativeSDK.login(externalUserId);
      // false = plain iOS permission prompt, no fall-through to Settings.
      nativeSDK.Notifications.requestPermission(false);
    } else {
      if (!webSDK) return; // init failed (already warned)
      await webSDK.login(externalUserId);
      console.info('[push] OneSignal user association set.');
      // Permission and subscription are SEPARATE in OneSignal v16. If the
      // browser permission is already granted (e.g. enabled via the padlock),
      // the slidedown would no-op and no push token would ever be created --
      // opt straight in instead. Only soft-ask when permission is undecided.
      try {
        const perm = typeof Notification !== 'undefined' ? Notification.permission : 'default';
        if (perm === 'granted') {
          await webSDK.User.PushSubscription.optIn();
          console.info('[push] permission already granted; opted in to push.');
        } else if (perm === 'default') {
          await webSDK.Slidedown.promptPush(); // self-caps re-prompt frequency
        } // 'denied': nothing we can do from code
      } catch { /* blocked/denied is fine */ }
    }
  } catch (e) {
    console.warn('[push] OneSignal login failed (non-fatal):', e);
  }
}

/** Detach this device from the user so a shared device never gets someone
 *  else's pushes. */
export async function pushLogout(): Promise<void> {
  if (!APP_ID || !initPromise) return;
  await initPromise;
  try {
    if (Capacitor.isNativePlatform()) {
      nativeSDK?.logout();
    } else {
      await webSDK?.logout();
    }
  } catch (e) {
    console.warn('[push] OneSignal logout failed (non-fatal):', e);
  }
}
